import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '@/lib/supabase'
import { Job } from '@/components/JobCard'

// Legacy hook - use useJobsOptimized for better performance

interface UseJobsParams {
  searchTerm: string
  selectedRole: string
  selectedLocation: string
  selectedProjectType: string
  selectedDateFilter: string
  currentPage: number
  limit?: number
}

export function useJobs({
  searchTerm,
  selectedRole,
  selectedLocation,
  selectedProjectType,
  selectedDateFilter,
  currentPage,
  limit = 12,
}: UseJobsParams) {
  const [jobs, setJobs] = useState<Job[]>([])
  const [loading, setLoading] = useState(true)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  const fetchJobs = useCallback(async () => {
    setLoading(true)

    const offset = (currentPage - 1) * limit

    try {
      // Build base query
      let query = supabase
        .from('jobs')
        .select('*')
        .eq('status', 'active')

      // Apply filters to query
      if (selectedProjectType && selectedProjectType !== 'all') {
        query = query.eq('project_type', selectedProjectType)
      }

      if (selectedLocation && selectedLocation !== 'all') {
        query = query.ilike('location', `%${selectedLocation}%`)
      }

      // Add role filtering
      if (selectedRole && selectedRole !== 'all') {
        query = query.eq('role', selectedRole)
      }

      if (searchTerm) {
        // Validate search term to prevent injection attacks
        if (!/^[a-zA-Z0-9\s\-_.,!?]+$/.test(searchTerm)) {
          console.error('Invalid search term detected')
          setLoading(false)
          return
        }
        const searchFilter = `title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`
        query = query.or(searchFilter)
      }

      // Build count query with same filters for consistency
      let countQuery = supabase
        .from('jobs')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active')

      // Apply same filters to count query
      if (selectedProjectType && selectedProjectType !== 'all') {
        countQuery = countQuery.eq('project_type', selectedProjectType)
      }

      if (selectedLocation && selectedLocation !== 'all') {
        countQuery = countQuery.ilike('location', `%${selectedLocation}%`)
      }

      if (selectedRole && selectedRole !== 'all') {
        countQuery = countQuery.eq('role', selectedRole)
      }

      if (searchTerm) {
        const searchFilter = `title.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`
        countQuery = countQuery.or(searchFilter)
      }

      // Get the total count with filters applied
      const { count: totalCountResult, error: countError } = await countQuery

      if (countError) {
        console.error('Error getting total count:', countError)
        setLoading(false)
        return
      }

      // Then get the paginated results
      const { data, error } = await query
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1)

      if (error) {
        console.error('Error fetching jobs:', error)
        console.error('Jobs error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        })
        setLoading(false)
        return
      }

      console.log('useJobs: Jobs data received:', data);

      if (data && data.length > 0) {
        setJobs(data)
        setTotalCount(totalCountResult || 0)
        setTotalPages(Math.ceil((totalCountResult || 0) / limit))
        console.log('useJobs: Jobs set successfully, count:', data.length);
      } else {
        setJobs([])
        setTotalCount(0)
        setTotalPages(0)
        console.log('useJobs: No jobs found, setting empty state');
      }
    } catch (error) {
      console.error('useJobs: Unexpected error during fetch:', error);
    } finally {
      setLoading(false)
    }
  }, [searchTerm, selectedRole, selectedLocation, selectedProjectType, selectedDateFilter, currentPage, limit])

  useEffect(() => {
    console.log('useJobs: useEffect triggered, calling fetchJobs');
    fetchJobs()
  }, [fetchJobs])

  return { jobs, loading, totalCount, totalPages, currentPage }
}
